'use client'

import { popModal, pushModal } from '@/components/modals'
import { Button } from '@/components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useConfirm } from '@/hooks/use-confirm'
import { useCurrentUser } from '@/hooks/use-current-user'
import type { TEmailTemplate } from '@/lib/types'
import { EMAIL_PRICE_PER_RECIPIENT } from '@/lib/constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { InfoIcon, Loader2, Sparkles } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'
import { GradientTooltip } from '../common/gradient-tooltip'
import { cn } from '@/lib/utils'

const emailSchema = z.object({
  recipients: z.string().optional(),
  subject: z.string().min(1, 'Subject is required'),
  message: z.string().optional(),
})

type EmailFormData = z.infer<typeof emailSchema>

interface Customer {
  email: string
  email_consent?: boolean
  first_name?: string
  last_name?: string
  [key: string]: any
}

interface SendEmailModalProps {
  defaultSubject?: string
  defaultMessage?: string
  selectedCustomers?: Customer[]
  projectId?: string
  onSuccess?: () => void
  totalCustomerCount?: number // Total count when sending to all customers
  hasCustomersTable?: boolean // Whether customers table exists (defaults to true)
}

export default function SendEmailModal({
  defaultSubject = '',
  defaultMessage = '',
  selectedCustomers = [],
  projectId: propsProjectId,
  onSuccess,
  totalCustomerCount,
  hasCustomersTable = true,
}: SendEmailModalProps) {
  const t = useTranslations('modals.send-email')
  const { data: user } = useCurrentUser()
  const { confirm } = useConfirm()
  const params = useParams()
  // Use the passed projectId prop, fallback to params.id for backward compatibility
  const projectId = propsProjectId || (params?.id as string | undefined)

  const [isLoading, setIsLoading] = useState(false)
  const [recipientMode, setRecipientMode] = useState<'all' | 'custom'>(
    // Default to 'all' since most people will use all contacts
    selectedCustomers.length > 0 || !hasCustomersTable ? 'custom' : 'all',
  )
  const [emailContent, setEmailContent] = useState(defaultMessage)
  const [isGenerating, setIsGenerating] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [saveAsTemplate, setSaveAsTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templates, setTemplates] = useState<TEmailTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [editorKey, setEditorKey] = useState(0)

  // Generate initial recipients string from selected customers
  const initialRecipients = selectedCustomers
    .filter((customer) => customer.email)
    .map((customer) => customer.email)
    .join('\n')

  const customersWithoutConsent = selectedCustomers.filter(
    (customer) => customer.email && !customer.email_consent,
  )
  const hasConsentWarning = customersWithoutConsent.length > 0

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      recipients: initialRecipients,
      subject: defaultSubject,
      message: defaultMessage,
    },
  })

  useEffect(() => {
    if (projectId) {
      loadTemplates()
    }
  }, [projectId])

  const loadTemplates = async () => {
    if (!projectId) return

    try {
      const response = await fetch(
        `/api/me/project/${projectId}/email-templates`,
      )
      if (response.ok) {
        const data = await response.json()
        setTemplates(data.data?.templates || data.templates || [])
      }
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId)
    const template = templates.find((t) => t.id === templateId)
    if (template) {
      form.setValue('subject', template.subject)
      setEmailContent(template.bodyHtml)
      setEditorKey((prev) => prev + 1) // Force editor remount with new content
    }
  }

  const handleGenerateEmail = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt for AI generation')
      return
    }

    setIsGenerating(true)
    try {
      const response = await fetch('/api/email/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: aiPrompt,
          context:
            recipientMode === 'all' && selectedCustomers.length > 0
              ? `Sending to ${selectedCustomers.length} customers`
              : undefined,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(
          result.message || result.error || 'Failed to generate email',
        )
      }

      form.setValue('subject', result.data?.subject || result.subject)
      setEmailContent(result.data?.bodyHtml || result.bodyHtml)
      setEditorKey((prev) => prev + 1) // Force editor remount with new content
      setAiPrompt('')
      toast.success('Email generated successfully!')
    } catch (error) {
      console.error('Error generating email:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate email',
      )
    } finally {
      setIsGenerating(false)
    }
  }

  /**
   * Parses recipients from textarea (one email per line)
   * @param recipientsData - String with one email per line
   * @returns Array of recipient objects with email
   */
  const parseRecipients = (recipientsData: string) => {
    const lines = recipientsData.trim().split('\n').filter(Boolean)
    const recipients = []

    for (const line of lines) {
      const email = line.trim()

      if (!email || !z.string().email().safeParse(email).success) {
        continue
      }

      recipients.push({ email })
    }

    return recipients
  }

  /**
   * Calculates the number of recipients based on current mode
   * @returns Number of recipients that will receive the email
   */
  const getRecipientCount = () => {
    if (recipientMode === 'all') {
      // If we have selected customers, use that count
      if (selectedCustomers.length > 0) {
        return selectedCustomers.filter((customer) => customer.email).length
      }
      // Otherwise, use the total customer count if provided
      return totalCustomerCount || 0
    } else {
      const recipients = form.watch('recipients')
      if (!recipients || !recipients.trim()) return 0
      return parseRecipients(recipients).length
    }
  }

  /**
   * Calculates the total price for sending emails
   * @returns Total cost in credits
   */
  const calculateTotalPrice = () => {
    return getRecipientCount() * EMAIL_PRICE_PER_RECIPIENT
  }

  /**
   * Checks if user has sufficient credits
   * @returns true if user has enough credits
   */
  const hasSufficientCredits = () => {
    if (!user) {
      return false
    }
    const totalPrice = calculateTotalPrice()
    return user.coin >= totalPrice
  }

  const saveTemplate = async (subject: string, bodyHtml: string) => {
    if (!projectId || !saveAsTemplate || !templateName.trim()) return

    try {
      const response = await fetch(
        `/api/me/project/${projectId}/email-templates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: templateName,
            subject,
            bodyHtml,
          }),
        },
      )

      if (response.ok) {
        toast.success(t('template_saved'))
        await loadTemplates()
      }
    } catch (error) {
      console.error('Failed to save template:', error)
      toast.error(t('template_save_error'))
    }
  }

  /**
   * Checks if the send button should be disabled
   * @returns true if the form is invalid or user has insufficient credits
   */
  const isSendDisabled = () => {
    const subject = form.watch('subject')
    const recipients = form.watch('recipients')

    // Check if subject or content is empty
    if (!subject || !subject.trim() || !emailContent || !emailContent.trim()) {
      return true
    }

    // If in custom mode, check if recipients input is empty
    if (recipientMode === 'custom' && (!recipients || !recipients.trim())) {
      return true
    }

    return false
  }

  /**
   * Sends a test email to the current user's email
   */
  const handleSendTest = async () => {
    if (!user?.email) {
      toast.error(t('no_user_email'))
      return
    }

    const subject = form.getValues('subject')
    if (!subject || !subject.trim()) {
      toast.error(t('subject_required'))
      return
    }

    if (!emailContent || !emailContent.trim()) {
      toast.error(t('content_required'))
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch('/api/email/send-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients: [{ email: user.email }],
          subject: `[TEST] ${subject}`,
          bodyHtml: emailContent,
          projectId,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || t('test_send_error'))
      }

      toast.success(t('test_send_success', { email: user.email }))
    } catch (error) {
      console.error('Error sending test email:', error)
      toast.error(error instanceof Error ? error.message : t('error'))
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handles cancel button click with confirmation if email has content
   */
  const handleCancel = async () => {
    const subject = form.watch('subject')
    const hasContent =
      (subject && subject.trim()) || (emailContent && emailContent.trim())

    if (hasContent) {
      const isConfirmed = await confirm({
        title: t('discard_changes_title'),
        description: t('discard_changes_description'),
        okText: t('discard'),
        cancelText: t('keep_editing'),
      })

      if (!isConfirmed) {
        return
      }
    }

    popModal()
  }

  const onSubmit = async (data: EmailFormData) => {
    if (!hasSufficientCredits()) {
      pushModal('PricingModal', {
        type: 'out_of_credit',
      })
      return
    }
    setIsLoading(true)
    try {
      let recipients: { email: string }[] = []

      // Get recipients based on mode
      if (recipientMode === 'all') {
        recipients = selectedCustomers
          .filter((customer) => customer.email)
          .map((customer) => ({ email: customer.email }))
      } else {
        // Custom mode - parse from textarea
        if (!data.recipients || data.recipients.trim() === '') {
          throw new Error(t('no_recipients_found'))
        }
        recipients = parseRecipients(data.recipients)
      }

      if (recipients.length === 0) {
        throw new Error(t('no_recipients_found'))
      }

      // Use batch endpoint for sending emails
      const response = await fetch('/api/email/send-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients,
          subject: data.subject,
          bodyHtml: emailContent,
          projectId,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || t('batch_send_error'))
      }

      // Show results
      if (result.data?.failed > 0) {
        toast.warning(
          t('batch_send_partial', {
            successful: result.data.successful,
            failed: result.data.failed,
          }),
        )
      }

      if (saveAsTemplate) {
        await saveTemplate(data.subject, emailContent)
      }

      toast.success(
        t('batch_send_success', {
          count: recipients.length,
        }),
      )
      popModal()
      onSuccess?.()
    } catch (error) {
      console.error('Error sending email:', error)
      toast.error(error instanceof Error ? error.message : t('error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DialogContent
      className="max-w-3xl max-h-[90vh] overflow-y-auto border-white/10"
      onInteractOutside={(e) => e.preventDefault()}
    >
      <DialogHeader className="pb-3 border-b border-white/10">
        <DialogTitle className="text-lg font-bold text-white mb-0.5">
          {t('bulk_title')}
        </DialogTitle>
        <DialogDescription className="text-xs text-white/60">
          {t('bulk_description')}
        </DialogDescription>
      </DialogHeader>

      {hasConsentWarning && (
        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <span className="text-yellow-400 text-xl">⚠️</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-yellow-300 mb-1.5">
                {t('consent_warning_title')}
              </h3>
              <p className="text-sm text-yellow-200/80 leading-relaxed">
                {t('consent_warning_text', {
                  without: customersWithoutConsent.length,
                  total: selectedCustomers.length,
                })}
                <br />
                <span className="font-medium">{t('consent_warning_spam')}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasSufficientCredits() && getRecipientCount() > 0 && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <span className="text-red-400 text-xl">🚫</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-red-300 mb-1.5">
                {t('insufficient_credits_title')}
              </h3>
              <p className="text-sm text-red-200/80 leading-relaxed">
                {t('insufficient_credits_text', {
                  required: calculateTotalPrice().toFixed(2),
                  current: user?.coin?.toFixed(2) || '0.00',
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-2 w-full max-w-full mt-3"
        >
          {projectId && templates.length > 0 && (
            <div className="space-y-2 p-3 bg-white/5 rounded-lg border border-white/10">
              <Label className="text-sm font-semibold text-white/90">
                {t('load_template')}
              </Label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  <SelectValue placeholder={t('select_template_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2 p-3 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-white/90">
                {t('recipients_label')}
              </Label>
              {getRecipientCount() > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white/90">
                    {getRecipientCount()}{' '}
                    {getRecipientCount() === 1
                      ? t('email_singular')
                      : t('email_plural')}
                  </span>
                  <span className="text-xs text-white/50">
                    ({EMAIL_PRICE_PER_RECIPIENT} {t('credit_plural')}{' '}
                    {t('each')})
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {!hasCustomersTable ? (
                <GradientTooltip content={t('all_recipients_disabled_tooltip')}>
                  <button
                    type="button"
                    disabled={!hasCustomersTable}
                    className={cn(
                      'p-2.5 rounded-lg border-2 text-left transition-all',
                      recipientMode === 'all'
                        ? 'bg-white/10 border-white/30 text-white'
                        : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/5 hover:text-white/90',
                      !hasCustomersTable && 'opacity-50 cursor-not-allowed',
                    )}
                    onClick={() => {
                      if (hasCustomersTable) setRecipientMode('all')
                    }}
                  >
                    <div className="font-medium text-sm mb-1">
                      {t('all_recipients')}
                    </div>
                    <div className="text-xs text-white/60">
                      {t('all_recipients_description')}
                    </div>
                  </button>
                </GradientTooltip>
              ) : (
                <button
                  type="button"
                  className={cn(
                    'p-2.5 rounded-lg border-2 text-left transition-all',
                    recipientMode === 'all'
                      ? 'bg-white/10 border-white/30 text-white'
                      : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/5 hover:text-white/90',
                  )}
                  onClick={() => setRecipientMode('all')}
                >
                  <div className="font-medium text-sm mb-0.5">
                    {t('all_recipients')}
                  </div>
                  <div className="text-xs text-white/60">
                    {t('all_recipients_description')}
                  </div>
                </button>
              )}
              <button
                type="button"
                className={cn(
                  'p-2.5 rounded-lg border-2 text-left transition-all',
                  recipientMode === 'custom'
                    ? 'bg-white/10 border-white/30 text-white'
                    : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/5 hover:text-white/90',
                )}
                onClick={() => setRecipientMode('custom')}
              >
                <div className="font-medium text-sm mb-0.5">
                  {t('custom_recipients')}
                </div>
                <div className="text-xs text-white/60">
                  {t('custom_recipients_description')}
                </div>
              </button>
            </div>

            {recipientMode === 'all' && selectedCustomers.length > 0 && (
              <div className="mt-2.5 p-2.5 rounded-lg bg-white/5 border border-white/10 space-y-1.5">
                <p className="text-xs font-medium text-white/80">
                  {t('selected_customers', {
                    count: selectedCustomers.length,
                  })}
                </p>
                <div className="max-h-[80px] overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  {selectedCustomers.slice(0, 8).map((customer) => (
                    <div
                      key={customer.email}
                      className="text-xs text-white/60 flex items-center gap-2"
                    >
                      <div className="w-1 h-1 rounded-full bg-white/30" />
                      {customer.first_name || customer.last_name
                        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                        : customer.email}
                      {customer.first_name || customer.last_name ? (
                        <span className="text-xs text-white/40">
                          ({customer.email})
                        </span>
                      ) : null}
                    </div>
                  ))}
                  {selectedCustomers.length > 8 && (
                    <p className="text-xs text-white/40 italic pl-2.5">
                      ... and {selectedCustomers.length - 8} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {recipientMode === 'custom' && (
              <FormField
                control={form.control}
                name="recipients"
                render={({ field }) => (
                  <FormItem className="mt-3">
                    <FormControl>
                      <Textarea
                        placeholder={t('recipients_placeholder')}
                        className="min-h-[100px] resize-none font-mono text-sm w-full bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:bg-white/10 focus:border-white/20"
                        {...field}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <p className="text-xs text-white/50 mt-1.5">
                      {t('recipients_help')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <div className="space-y-2.5 p-3 bg-white/5 rounded-lg border border-white/10">
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-white/90">
                    {t('subject')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('subject_placeholder')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:bg-white/10 focus:border-white/20 h-9"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center justify-between">
              <FormLabel className="text-sm font-semibold text-white/90">
                {t('message')}
              </FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2.5 text-xs bg-gold/10 text-gold hover:bg-gold/20 hover:text-gold border border-gold/30 hover:border-gold/50"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                        {t('generating')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-1.5 h-3 w-3" />
                        {t('generate_with_ai')}
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 bg-[#1a1a1a] border-white/20 p-3"
                  align="end"
                  disablePortal
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-gold" />
                      <span className="text-sm font-medium text-white/90">
                        {t('generate_with_ai')}
                      </span>
                    </div>
                    <Textarea
                      placeholder={t('ai_prompt_placeholder')}
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      className="min-h-[80px] w-full bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:bg-white/10 focus:border-white/20 text-sm"
                      disabled={isGenerating}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          handleGenerateEmail()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={handleGenerateEmail}
                      disabled={isGenerating || !aiPrompt.trim()}
                      size="sm"
                      className="w-full bg-gold-gradient hover:opacity-90 text-black border-0 shadow-gold-glow"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('generating')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          {t('generate')}
                        </>
                      )}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="rounded-lg overflow-hidden border border-white/10">
              <MinimalTiptapEditor
                key={editorKey}
                value={emailContent}
                onChange={(value) => {
                  if (typeof value === 'string') {
                    setEmailContent(value)
                  }
                }}
                editorContentClassName="p-2.5 min-h-[200px] bg-white/5"
                className="min-h-[200px]"
                modal={true}
              />
            </div>
          </div>

          {/* disable for now */}
          {projectId && false && (
            <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center space-x-2">
                <Switch
                  id="save-template"
                  checked={saveAsTemplate}
                  onCheckedChange={setSaveAsTemplate}
                />
                <Label htmlFor="save-template" className="cursor-pointer">
                  {t('save_as_template')}
                </Label>
              </div>
              {saveAsTemplate && (
                <Input
                  placeholder={t('template_name_placeholder')}
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              )}
            </div>
          )}

          <div className="space-y-2.5 pt-3 border-t border-white/10">
            {/* Action Buttons - Compact Layout */}
            <div className="flex flex-col sm:flex-row gap-2.5 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="border-white/20 bg-white/5 text-white/90 hover:bg-white/10 hover:text-white hover:border-white/30 text-sm font-medium h-9"
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendTest}
                disabled={isLoading || isSendDisabled()}
                className="border-white/20 bg-white/5 text-white/90 hover:bg-white/10 hover:text-white hover:border-white/30 text-sm font-medium h-9"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('send_test')
                )}
              </Button>
              <Button
                type="submit"
                disabled={isLoading || isSendDisabled()}
                className="bg-gold-gradient hover:opacity-90 text-black border-0 shadow-gold-glow font-semibold text-sm h-9 min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('send_all')
                )}
              </Button>
            </div>
            {/* Send Test Description - Compact */}
            <div className="text-xs text-white/50 text-right">
              {t('send_email_description')}{' '}
              <span className="text-white/70 font-medium">
                {user?.email || 'your email address'}
              </span>{' '}
              ({EMAIL_PRICE_PER_RECIPIENT} {t('credit_plural')} {t('each')})
            </div>
          </div>
        </form>
      </Form>
    </DialogContent>
  )
}
